import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { ItemFormDialog } from "@/components/ItemFormDialog";
import { STATUS_CONFIG, fmtDate, daysLabel } from "@/lib/status";
import { Plus, Search, ShieldCheck, X } from "lucide-react";

const KPI_DEFS = [
  { key: "overdue", label: "Overdue", accent: "bg-[hsl(var(--status-overdue))]" },
  { key: "due_soon", label: "Due soon", accent: "bg-[hsl(var(--status-due-soon))]" },
  { key: "upcoming", label: "Upcoming", accent: "bg-[hsl(var(--status-upcoming))]" },
  { key: "total", label: "Total items", accent: "bg-border" },
];

const GROUP_ORDER = ["overdue", "due_soon", "upcoming", "renewed"];
const GROUP_TITLES = {
  overdue: "Overdue",
  due_soon: "Due soon",
  upcoming: "Upcoming",
  renewed: "Renewed & archived",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    const [itemsRes, catsRes] = await Promise.all([
      api.get("/items"),
      api.get("/categories"),
    ]);
    setItems(itemsRes.data.items);
    setCategories(catsRes.data.categories);
  }, []);

  useEffect(() => {
    load().catch(() => setItems([]));
  }, [load]);

  const counts = useMemo(() => {
    const c = { overdue: 0, due_soon: 0, upcoming: 0, renewed: 0, total: items?.length || 0 };
    (items || []).forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let r = items;
    if (statusFilter !== "all") r = r.filter((i) => i.status === statusFilter);
    if (categoryFilter !== "all") r = r.filter((i) => i.category_id === categoryFilter);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      r = r.filter((i) =>
        i.name.toLowerCase().includes(q) || i.issuer.toLowerCase().includes(q) ||
        i.notes.toLowerCase().includes(q) || i.category_name.toLowerCase().includes(q)
      );
    }
    return r;
  }, [items, statusFilter, categoryFilter, search]);

  const grouped = useMemo(() => {
    const g = {};
    GROUP_ORDER.forEach((k) => { g[k] = []; });
    filtered.forEach((i) => { (g[i.status] = g[i.status] || []).push(i); });
    return g;
  }, [filtered]);

  const hasFilters = statusFilter !== "all" || categoryFilter !== "all" || search.trim() !== "";
  const loading = items === null;
  const firstName = (user?.name || "").split(" ")[0];

  const ItemRows = ({ list }) => (
    <>
      {/* Desktop table */}
      <div className="hidden md:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Issued by</TableHead>
                <TableHead className="text-right">Due date</TableHead>
                <TableHead className="text-right">Timeline</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((item) => (
                <TableRow key={item.id} onClick={() => navigate(`/items/${item.id}`)}
                  className="cursor-pointer hover:bg-muted/60" data-testid={`item-row-${item.id}`}>
                  <TableCell className="font-medium max-w-[260px] truncate">{item.name}</TableCell>
                  <TableCell className="text-muted-foreground">{item.category_name}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[180px] truncate">{item.issuer || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtDate(item.due_date)}</TableCell>
                  <TableCell className={`text-right text-sm tabular-nums ${STATUS_CONFIG[item.status]?.text || ""}`}>
                    {daysLabel(item)}
                  </TableCell>
                  <TableCell><StatusBadge status={item.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {list.map((item) => (
          <Card key={item.id} onClick={() => navigate(`/items/${item.id}`)}
            className="cursor-pointer active:bg-muted/60 transition-colors" data-testid={`item-card-${item.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.category_name}{item.issuer ? ` · ${item.issuer}` : ""}</p>
                </div>
                <StatusBadge status={item.status} />
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="tabular-nums text-muted-foreground">{fmtDate(item.due_date)}</span>
                <span className={`tabular-nums font-medium ${STATUS_CONFIG[item.status]?.text || ""}`}>{daysLabel(item)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="dashboard-title">
            {firstName ? `Good to see you, ${firstName}` : "Dashboard"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {counts.overdue > 0
              ? `${counts.overdue} item${counts.overdue !== 1 ? "s" : ""} need${counts.overdue === 1 ? "s" : ""} attention.`
              : counts.due_soon > 0
                ? `${counts.due_soon} renewal${counts.due_soon !== 1 ? "s" : ""} coming up soon.`
                : "Everything is on track."}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="rounded-xl hidden md:inline-flex" data-testid="dashboard-add-item-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add item
        </Button>
      </div>

      {/* KPI cards */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {loading
          ? KPI_DEFS.map((k) => (
            <Card key={k.key}><CardContent className="p-4 sm:p-5"><Skeleton className="h-4 w-20 mb-3" /><Skeleton className="h-8 w-12" /></CardContent></Card>
          ))
          : KPI_DEFS.map((k) => {
            const active = statusFilter === k.key;
            const value = k.key === "total" ? counts.total : counts[k.key];
            return (
              <button key={k.key}
                onClick={() => k.key !== "total" && setStatusFilter(active ? "all" : k.key)}
                className="text-left" data-testid={`status-count-${k.key}`}>
                <Card className={`relative overflow-hidden transition-shadow hover:shadow-sm ${active ? "ring-2 ring-[hsl(var(--ring))]" : ""}`}>
                  <span className={`absolute left-0 top-0 h-full w-1 ${k.accent}`} />
                  <CardContent className="p-4 sm:p-5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{k.label}</p>
                    <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
      </div>

      {/* Search + filters */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items, issuers, notes…" className="pl-9 rounded-lg"
            data-testid="items-search-input" />
        </div>
        <div className="flex gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-[180px] rounded-lg" data-testid="items-category-filter-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[160px] rounded-lg" data-testid="items-status-filter-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="due_soon">Due soon</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="renewed">Renewed</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="icon" className="shrink-0" aria-label="Clear filters"
              onClick={() => { setSearch(""); setStatusFilter("all"); setCategoryFilter("all"); }}
              data-testid="items-clear-filters-button">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mt-6 space-y-8">
        {loading && (
          <Card><CardContent className="p-5 space-y-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent></Card>
        )}

        {!loading && items.length === 0 && (
          <EmptyState icon={ShieldCheck}
            title="Track your first expiry"
            body="Add an item like a permit, policy, or certification. BIIPP will email your team before it's due — so renewals never sneak up on you."
            ctaLabel="Add your first item"
            onCta={() => setAddOpen(true)}
            testId="dashboard-empty-state" />
        )}

        {!loading && items.length > 0 && filtered.length === 0 && (
          <EmptyState icon={Search}
            title="Nothing matches"
            body="Try a different search or clear the filters to see all your items."
            ctaLabel="Clear filters"
            onCta={() => { setSearch(""); setStatusFilter("all"); setCategoryFilter("all"); }}
            testId="dashboard-no-results" />
        )}

        {!loading && filtered.length > 0 && (
          hasFilters ? (
            <ItemRows list={filtered} />
          ) : (
            GROUP_ORDER.map((key) => {
              const list = grouped[key];
              if (!list || list.length === 0) return null;
              return (
                <section key={key} data-testid={`dashboard-group-${key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-base font-semibold tracking-tight">{GROUP_TITLES[key]}</h2>
                    <span className="text-xs text-muted-foreground tabular-nums">{list.length}</span>
                  </div>
                  <ItemRows list={list} />
                </section>
              );
            })
          )
        )}
      </div>

      {/* Mobile sticky add button */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t bg-background/95 backdrop-blur px-4 py-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <Button onClick={() => setAddOpen(true)} className="w-full rounded-xl" data-testid="dashboard-add-item-button-mobile">
          <Plus className="h-4 w-4 mr-1.5" /> Add item
        </Button>
      </div>

      <ItemFormDialog open={addOpen} onOpenChange={setAddOpen} categories={categories} onSaved={() => load()} />
    </div>
  );
}
